/**
 * SdiChangeConfirmDialog — F-002 Phase 4 (IMPL-P4-009).
 *
 * Dialog di conferma mostrato dal BillingProfileForm.onSubmit prima di
 * persistere il profilo SOLO quando:
 *   - `hasFattureEmesse === true`  (l'account ha già emesso fatture SDI)
 *   - sdiCode o sdiPec sono cambiati rispetto allo stato salvato
 *
 * Il dialog è una versione minimale "alert dialog" implementata senza il
 * pacchetto `@radix-ui/react-alert-dialog` (non installato in apps/web a
 * Phase 4) — usa un overlay full-screen, role="alertdialog", aria-modal,
 * trap focus minimale + ESC handler. Il pulsante sicuro "Annulla" riceve
 * il focus iniziale.
 *
 * Il caller è responsabile di chiudere il dialog (`onCancel` o `onConfirm`)
 * e di procedere con `save()` dopo `onConfirm`.
 */
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';

export interface SdiChangeConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SdiChangeConfirmDialog({ open, onConfirm, onCancel }: SdiChangeConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Focus sicuro su "Annulla" all'apertura.
  useEffect(() => {
    if (!open) return;
    // setTimeout 0 per assicurarsi che il nodo sia in DOM e focusabile.
    const t = setTimeout(() => cancelRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // ESC → annulla
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        // click sull'overlay = annulla (NON sul contenuto)
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="sdi-confirm-title"
        aria-describedby="sdi-confirm-desc"
        className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 id="sdi-confirm-title" className="text-lg font-semibold">
          Confermi la modifica dei dati SDI?
        </h2>
        <p id="sdi-confirm-desc" className="mt-2 text-sm text-muted-foreground">
          Stai modificando i dati SDI. Le fatture precedenti potrebbero richiedere note di credito
          per essere riassegnate al nuovo recapito.
        </p>

        <div className="mt-6 flex justify-end gap-2">
          <Button ref={cancelRef} type="button" variant="outline" onClick={onCancel}>
            Annulla
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm}>
            Conferma modifica
          </Button>
        </div>
      </div>
    </div>
  );
}
